/**
 * @render react
 * @name Input
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import './_style.scss';

/**
 * Input component
 *
 * @param {Object} props - the props object
 * @param {string} props.type - Type of the input. Values can be text/search/number/email/checkbox
 * @param {string} props.name - The name of the input
 * @param {string} props.value - Initial value
 * @param {string} props.placeholder - Placeholder to display. Not valid for radio and checkbox
 * @param {boolean} props.checked - Whether input is checked. Valid for radio and checkbox
 * @param {string} props.srLabel - accessibility label for Screen reader
 * @param {boolean} props.required - whether input is required
 * @param {Function} props.onChange - Callback when value changes
 * @param {Function} props.onClick - Callback when component is clicked
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling.
 * @param {Object} props.extraClassNames - Extra class names to be applied to the component
 * @param {Object} props.context - Extra information coming from Polaris provider.
 * @param {boolean} props.isControlled - Whether the input should behave as a controlled component
 */
class Input extends PureComponent {
  /**
  * Constructor
  */
  constructor(props) {
    super(props);
    const { checked, value } = this.props;

    this.state = {
      checked,
      internalValue: value, // Internal state for uncontrolled behavior
    };

    // Bind the methods
    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  /**
   * onChange event handle
   * @param {*} event - The event fired
   */
  handleChange(event) {
    const { onChange, isControlled } = this.props;
    const { value } = event.target;

    if (!isControlled) {
      this.setState({ internalValue: value });
    }

    if (onChange) {
      onChange(event);
    }
  }

  /**
   * onClick event handle
   * @param {*} event - The event fired
   */
  handleClick(event) {
    const { onClick } = this.props;

    if (onClick) {
      onClick(event);
    }
  }

  /**
   * Render our component
   */
  render() {
    const {
      type,
      name,
      placeholder,
      id,
      required,
      srLabel,
      loadStyles,
      extraClassNames,
      context: { cssPrefix },
      value,
      isControlled,
    } = this.props;
    const { checked, internalValue } = this.state;

    return (
      <input
        type={type}
        name={name || undefined}
        value={isControlled ? value : internalValue} // Use controlled or internal value
        id={id || undefined}
        placeholder={
          type !== 'checkbox' && type !== 'radio' && placeholder
            ? placeholder
            : null
        }
        checked={
          (type === 'checkbox' || type === 'radio') && checked ? 'checked' : null
        }
        onChange={this.handleChange || null}
        onClick={this.handleClick || null}
        className={classNames({
          [`${cssPrefix}__input`]: loadStyles,
          [`${cssPrefix}__input--${type}`]: true,
          ...extraClassNames,
        })}
        title={srLabel}
        aria-label={srLabel}
        required={required || false}
      />
    );
  }
}

/**
 * Valid props
 */
Input.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  checked: PropTypes.bool,
  required: PropTypes.bool,
  srLabel: PropTypes.string,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  isControlled: PropTypes.bool, // New prop to toggle controlled behavior
};

/**
 * Default props
 */
Input.defaultProps = {
  type: '',
  name: '',
  value: '',
  placeholder: '',
  id: '',
  checked: false,
  required: false,
  srLabel: 'Input field',
  onClick: () => {},
  onChange: () => {},
  loadStyles: true,
  extraClassNames: {},
  context: {},
  isControlled: false, // Default to uncontrolled behavior
};

export default withPolaris(Input);
